import React, { useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import Form from 'react-bootstrap/Form';

import { useLocalization } from '../../../contexts/LocalizationContext';
import RequiredIndicator from './RequiredIndicator';

const SelectField = React.forwardRef(function (
  {
    noOptionsMessage,
    isValidated,
    isMulti,
    placeholder,
    className,
    controlId,
    required,
    disabled,
    error,
    label,
    value,
    onChange,
    options,
    createOption,
    onCreate,
    ...props
  },

  ref
) {
  const { localize } = useLocalization();
  const [isValid, setIsValid] = useState(true);
  ref = ref || React.createRef();

  useEffect(() => {
    if (isValidated !== undefined) return;
    setIsValid(true);
  }, [value]);

  const getLabel = props.getOptionLabel || ((opt) => opt?.label || '');

  const sortedOptions = (createOption ? [createOption] : []).concat(
    [...options].sort((a, b) => localize.compare(getLabel(a), getLabel(b)))
  );

  const handleChange = (opt) => {
    const selectedCreateOption = !createOption
      ? null
      : (isMulti ? opt : [opt]).findLast(
          (selectedOpt) =>
            (props.getOptionValue?.(selectedOpt) || selectedOpt.value) ===
            (props.getOptionValue?.(createOption) || createOption.value)
        );

    if (selectedCreateOption) {
      return onCreate?.(selectedCreateOption);
    }

    onChange?.(opt);
  };

  return (
    <>
      <Form.Group className={`w-100 ${className || ''}`}>
        {label && (
          <Form.Label htmlFor={controlId}>
            {label}
            <RequiredIndicator required={required} />
          </Form.Label>
        )}

        <ReactSelect
          ref={ref}
          inputId={controlId}
          classNamePrefix='react-select-dropdown'
          isDisabled={disabled}
          isMulti={isMulti}
          value={value}
          onChange={handleChange}
          noOptionsMessage={() =>
            noOptionsMessage ||
            localize('common.results.none', { editable: false })
          }
          placeholder={
            placeholder || localize('common.select', { editable: false })
          }
          styles={
            (isValidated === undefined || isValidated === true) && !isValid
              ? {
                  control: (provided, state) => ({
                    ...provided,
                    borderColor: '#dc3545',
                    boxShadow: state.isFocused
                      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                      : '',
                    backgroundImage:
                      'url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 12%27 width=%2712%27 height=%2712%27 fill=%27none%27 stroke=%27%23dc3545%27%3e%3ccircle cx=%276%27 cy=%276%27 r=%274.5%27/%3e%3cpath stroke-linejoin=%27round%27 d=%27M5.8 3.6h.4L6 6.5z%27/%3e%3ccircle cx=%276%27 cy=%278.2%27 r=%27.6%27 fill=%27%23dc3545%27 stroke=%27none%27/%3e%3c/svg%3e")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition:
                      'right calc(0.375em + 0.1875rem) center',
                    backgroundSize:
                      'calc(0.75em + 0.375rem) calc(0.75em + 0.375rem)'
                  }),
                  dropdownIndicator: (provided, state) => ({
                    ...provided,
                    opacity: 0
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'black' // placeholder black for contrast
                  })
                }
              : {
                  control: (provided, state) => ({
                    ...provided,
                    borderColor: state.isFocused
                      ? '#f6ba82'
                      : provided.borderColor,
                    boxShadow: state.isFocused
                      ? '0 0 0 0.25rem rgba(236, 116, 5, 0.25)'
                      : provided.boxShadow
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'black' // placeholder black for contrast
                  })
                }
          }
          options={sortedOptions}
          {...props}
        />
        {!disabled && (
          <div style={{ position: 'absolute', height: 0, width: 0 }}>
            <label htmlFor={controlId + 'hidden'} className='d-none'>
              hidden
            </label>
            <input
              id={controlId + 'hidden'}
              required={required}
              tabIndex={-1}
              autoComplete='off'
              style={{
                opacity: 0,
                height: 0,
                padding: 0,
                margin: 0,
                border: 'none',
                position: 'relative'
              }}
              value={
                (isMulti && Array.isArray(value) && value.length > 0) ||
                (!isMulti && value)
                  ? 'value'
                  : ''
              }
              onChange={() => null}
              onFocus={() => ref?.current?.focus()}
              onInvalid={() => setIsValid(false)}
            />
          </div>
        )}
      </Form.Group>
    </>
  );
});

export default SelectField;
